import * as MCAV from "multicoin-address-validator";

//const WAValidator: Validator = require('multicoin-address-validator')

export enum AddressValidationResultCode {
    VALID = "VALID",
    INVALID = "INVALID",
    WRONG_CURRENCY = "WRONG_CURRENCY",
    CURRENCY_NOT_FOUND = "CURRENCY_NOT_FOUND"
}

export interface AddressValidationResult {
    result: AddressValidationResultCode
    guessedCurrencies?: string[]
}

const CUR_MAPPINGS: {[key: string]: string} = {
    "mbtc": "btc",
    "ubtc": "btc",
    "meth": "eth",
    "mltc": "ltc",
    "usdt.erc20": "usdt",
    "bsc" : "bnb"
}

/* If an address matches multiple coins we will only sugges from this list. */
const GUESS_SUGGESTIONS = [
    "btc", "bnb", "usdt", "doge", "eth", "ltc", "sol", "trx", "xmr"
]

export const toCanonicalSymbol = (currencySymbol: string): string => {
    const mappedTo = CUR_MAPPINGS[currencySymbol.toLowerCase()]

    if (mappedTo) {
        // console.log("mapped currency: %s -> %s", currencySymbol, mappedTo)
        return mappedTo
    } else {
        return currencySymbol.toLowerCase()
    }

}

/** BTC Lightning on mainnet alway starts with "lnbc". After that the amount and multiplier are optional. */
const BTC_LIGHNING_REGEX = /^lnbc.+/

const BTC_LIQUID_REGEX = /^VJL.+/

/** USDT and USDC, for example, are implemented on multiple tokens. This mapping provides
 * aliases for the given coin (key).
 */
const ALIASES: {[key: string]: string[]} = {
    "usdt": ["trx"],
    "usdc": ["trx"],
    "bsc" : ["bnb"]
}


/**
 * Test if the given address for the given coin is valid.
 * Optionally, if invalid, check against all coins and return a list of coins where the adress is valid.
 * @param address the address to check
 * @param currencySymbol the currency
 * @param guessCurrency set to true if an invalid address for the given currency should return a list of suggestions for matching coins
 * @param lightening set to true if the address is a BTC Lightning invoice
 * @returns the result and an optional list of matching coins in case the address was invalid
 */
export const validateCryptoAddress = (address: string, currencySymbol: string, guessCurrency: boolean, lightening: boolean) : AddressValidationResult => {
    currencySymbol = toCanonicalSymbol(currencySymbol);
    // shortcut BTC lightning addresses
    if (lightening  &&  address.match(BTC_LIGHNING_REGEX)) {
        return {result: AddressValidationResultCode.VALID}
    } else if (currencySymbol === "btc"  &&  address.match(BTC_LIQUID_REGEX)) {
        return {result: AddressValidationResultCode.VALID}
    }

    if (MCAV.findCurrency(currencySymbol) == null) {
        console.log("unable to validate address, currency not found: %s", currencySymbol)
        return {result: AddressValidationResultCode.CURRENCY_NOT_FOUND}
    }

    const aliases = ALIASES[currencySymbol] ?? []
    const symbolsToTest = [currencySymbol, ...aliases]

    for (let coin of symbolsToTest) {
        if (MCAV.validate(address, coin)) {
            return {result: AddressValidationResultCode.VALID}
        }
    }

    let guessedCurrencies = []

    if (guessCurrency) {
        for (const c of MCAV.getCurrencies()) {
            const v = MCAV.validate(address, c.symbol)

            if (v  &&  GUESS_SUGGESTIONS.includes(c.symbol.toLowerCase())) {
                guessedCurrencies.push(c.symbol)
            }
        }
    }

    let result

    if (guessedCurrencies.length > 0) {
        result = AddressValidationResultCode.WRONG_CURRENCY
    } else {
        result = AddressValidationResultCode.INVALID
    }

    return {result: result, guessedCurrencies: guessedCurrencies.length == 0 ? undefined : guessedCurrencies}
}